import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useBlocker } from 'react-router-dom';
import { Icon } from './Icons';

/**
 * While a game is running, leaving the page (sidebar, links, back button) asks first.
 * Confirming gives up the game (`onLeave`) and then continues to where the player wanted to go.
 */
export function LeaveGuard({ active, onLeave, text = 'Leaving now counts as giving up: you lose this game.' }: {
  active: boolean;
  onLeave: () => void;
  text?: string;
}) {
  const blocker = useBlocker(({ currentLocation, nextLocation }) => active && currentLocation.pathname !== nextLocation.pathname);

  // Closing or reloading the tab: the browser shows its own "leave site?" dialog.
  useEffect(() => {
    if (!active) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [active]);

  useEffect(() => {
    if (blocker.state === 'blocked' && !active) blocker.proceed();
  }, [blocker, active]);

  if (blocker.state !== 'blocked') return null;
  return createPortal(
    <div className="modal-backdrop" role="presentation" onClick={() => blocker.reset()}>
      <div className="modal-card card" role="alertdialog" aria-modal="true" aria-labelledby="leave-title" onClick={(e) => e.stopPropagation()}>
        <h2 id="leave-title"><Icon name="flag" size={18} /> Give up this game?</h2>
        <p className="muted">{text}</p>
        <div className="row gap wrap">
          <button className="btn primary" autoFocus onClick={() => blocker.reset()}>Keep playing</button>
          <button className="btn danger" onClick={() => { onLeave(); blocker.proceed(); }}>Give up and leave</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
